import {Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {HasElementKey, HasSystemKey} from '../../../Model/Catalog/NextPimField';
import {ExcelElement} from '../../../Model/ui/ExcelElement';
import {Select} from '../../../Model/ui/Select';
import {WawiList} from '../../../Model/wawi/WawiList';
import {LoginService} from '../../../Services/login.service';
import {TemplateService} from '../../../Services/template.service';
import {WawiListService} from '../../../Services/wawiList.service';
import {TranslateService} from '@ngx-translate/core';
import {ValueChangedEvent} from 'devextreme/ui/text_box';
import {
    Column,
    RowClickEvent,
    RowDraggingAddEvent,
    RowDraggingReorderEvent,
    RowDraggingStartEvent
} from 'devextreme/ui/data_grid';
import {HidingEvent} from 'devextreme/ui/popover';
import {ClickEvent} from 'devextreme/ui/button';
import {confirm} from 'devextreme/ui/dialog';
import {NpInputControlContext} from "../../../ui/inputControl/np-input-control-context";

const hasPimField = (column: ExcelElement, fieldKey) => {
    return column.pimFields.map(pimField => {
        let result = pimField.field;
        if (HasSystemKey(pimField)) {
            result += '_' + pimField.systemKey;
            if (HasElementKey(pimField)) {
                result += '_' + pimField.elementKey;
            }
        }

        return result;
    }).some(y => y == fieldKey);
}

@Component({
    selector: 'templateExcel',
    templateUrl: 'templateExcel.component.html',
    styleUrls: ['template.css'],
    standalone: false
})
export class TemplateExcelComponent implements OnInit, OnChanges {
    private _excelModel: ExcelElement[] = [];
    filteredExcelModel: ExcelElement[] = [];

    @Input()
    set excelModel(value: ExcelElement[]) {
        this._excelModel = value;
        this.filteredExcelModel = value.filter(e => this.exportMode || e.title?.length >= 1);
    }

    get excelModel(): ExcelElement[] {
        return this._excelModel;
    }
    @Input() showToolTips: Boolean = false;
    @Input() showProToolTips: Boolean = false;
    @Input() showPlaceHolders: Boolean = true;
    @Input() exportMode: Boolean = false;

    @Output() rowSelected = new EventEmitter<string>();
    @Output() removeTemplate = new EventEmitter<string>();
    @Output() updateValue = new EventEmitter<string>();
    @Output() reorderModel = new EventEmitter<any>();
    @Output() replaceItem = new EventEmitter<any>();

    context = inject(NpInputControlContext);
    private _focusedRowKey: string | undefined;

    rowId = '';

    rowSelection: string[] = [];

    mappings: WawiList[] = [];
    calculationImport: Select[] = [];
    calculationExport: Select[] = [];
    numberformat: Select[] = [];
    seperator: Select[] = [];
    operators: Select[] = [];

    columns: Column[] = [];

    public popoverVisible: boolean = false;
    public popoverElement: ExcelElement | null = null;
    public popoverTarget: HTMLElement | null = null;

    constructor(
        private translate: TranslateService,
        public loginService: LoginService,
        wawiListService: WawiListService,
        public templateService: TemplateService
    ) {
        this.mappings.push(new WawiList(null, '', null, null, null, null));
        wawiListService.getAllListsWithoutFields(loginService.currentCustomer.id).subscribe((result) => {
            result.forEach((wawiList) => {
                if (wawiList.isMapping) {
                    this.mappings.push(wawiList);
                }
            });
        });
    }

    ngOnInit(): void {
        this.context.selectedElementsChanged.subscribe((x) => this.selectItemsFromSelectedPimFields(x));
        this.calculationImport = [
            new Select('', '', ''),
            new Select(this.translate.instant('g in kg'), this.translate.instant('kg in g'), 'GINKG'),
            new Select(this.translate.instant('t in kg'), this.translate.instant('kg in t'), 'TINKG'),
            new Select(this.translate.instant('mm in m'), this.translate.instant('m in mm'), 'MMINM'),
            new Select(this.translate.instant('cm in m'), this.translate.instant('m in cm'), 'CMINM'),
            new Select(this.translate.instant('Stück in C62'), this.translate.instant('C62 in Stück'), 'STKINC62'),
            new Select(this.translate.instant('Faktor'), this.translate.instant('Faktor'), 'FACTOR')
        ];
        this.calculationExport = [
            new Select('', '', ''),
            new Select(this.translate.instant('g in kg'), this.translate.instant('kg in g'), 'GINKG'),
            new Select(this.translate.instant('t in kg'), this.translate.instant('kg in t'), 'TINKG'),
            new Select(this.translate.instant('mm in m'), this.translate.instant('m in mm'), 'MMINM'),
            new Select(this.translate.instant('cm in m'), this.translate.instant('m in cm'), 'CMINM'),
            new Select(this.translate.instant('Faktor'), this.translate.instant('Faktor'), 'FACTOR')
        ];
        this.numberformat = [
            new Select('', '', ''),
            new Select('1.900,00', '1.900,00', ','),
            new Select('1,900.00', '1,900.00', '.')
        ];
        this.seperator = [
            new Select('', '', ''),
            new Select(this.translate.instant('Leerzeichen'), this.translate.instant('Leerzeichen'), ' '),
            new Select('_', '_', '_'),
            new Select('|', '|', '|'),
            new Select(',', ',', ','),
            new Select('.', '.', '.'),
            new Select(';', ';', ';')
        ];
        this.operators = [
            new Select('', '', 'NONE'),
            new Select(this.translate.instant('Addieren'), this.translate.instant('Addieren'), 'ADDITION'),
            new Select(this.translate.instant('Subtrahieren'), this.translate.instant('Subtrahieren'), 'SUBTRACTION'),
            new Select(this.translate.instant('Multiplizieren'), this.translate.instant('Multiplizieren'), 'MULTIPLICATION'),
            new Select(this.translate.instant('Dividieren'), this.translate.instant('Dividieren'), 'DIVISION')
        ];

        if (this.exportMode) {
            this.columns = [
                {
                    cellTemplate: 'exportCellTemplate',
                    editCellTemplate: 'exportCellEditTemplate',
                    allowEditing: true,
                    dataField: 'title', // DevExtreme benötigt ein dataField für die Bearbeitung
                    setCellValue: (newData, value, currentRowData) => {
                        // Diese Funktion wird aufgerufen, wenn die Bearbeitung abgeschlossen ist
                        // Da wir custom templates verwenden, müssen wir nichts tun
                        // Die Werte werden bereits in den dx-text-box onChange-Events aktualisiert
                    },
                    calculateFilterExpression: (filterValue, selectedFilterOperation, target) => {
                        if (target === 'search') {
                            return [
                                ['displayName', 'contains', filterValue],
                                'or',
                                ['title', 'contains', filterValue],
                                'or',
                                ['pimFields', 'contains', {field: filterValue},],
                                'or',
                                ['pimFields', 'contains', {systemKey: filterValue}],
                                'or',
                                ['pimFields', 'contains', {elementKey: filterValue,}],
                            ];
                        }

                        return null;
                    }
                },
                {
                    type: 'buttons',
                    buttons: [
                        {
                            icon: 'preferences',
                            hint: this.translate.instant('Feldeinstellungen bearbeiten'),
                            visible: ({row}) => !!this.hasTemplateItemSettings(row.data),
                            cssClass: 'primary',
                            onClick: (e) => this.onOpenFieldSettings(e, e.row.data),
                        },
                        {
                            icon: 'preferences',
                            hint: this.translate.instant('Feldeinstellungen bearbeiten'),
                            visible: ({row}) => !this.hasTemplateItemSettings(row.data),
                            cssClass: 'secondary',
                            onClick: (e) => this.onOpenFieldSettings(e, e.row.data),
                        },
                        {
                            icon: 'trash',
                            hint: this.translate.instant('Entfernen'),
                            visible: ({row}) => row.data.pimFields && row.data.pimFields.length > 0,
                            onClick: (e) => this.removeClicked(e.row.data),
                        }
                    ]
                }
            ];
        } else {
            this.columns = [
                {
                    cellTemplate: 'importCellTemplate',
                    calculateFilterExpression: (filterValue, selectedFilterOperation, target) => {
                        if (target === 'search') {
                            return [
                                ['title', 'contains', filterValue],
                                'or',
                                ['row1', 'contains', filterValue],
                                'or',
                                ['row2', 'contains', filterValue],
                                'or',
                                ['pimFields', 'contains', {field: filterValue},],
                                'or',
                                ['pimFields', 'contains', {systemKey: filterValue}],
                                'or',
                                ['pimFields', 'contains', {elementKey: filterValue,}],
                            ];
                        }

                        return null;
                    }
                },
                {
                    type: 'buttons',
                    buttons: [
                        {
                            icon: 'preferences',
                            hint: this.translate.instant('Feldeinstellungen bearbeiten'),
                            visible: ({row}) => !!this.hasTemplateItemSettings(row.data),
                            cssClass: 'primary',
                            onClick: (e) => this.onOpenFieldSettings(e, e.row.data),
                        },
                        {
                            icon: 'preferences',
                            hint: this.translate.instant('Feldeinstellungen bearbeiten'),
                            visible: ({row}) => !this.hasTemplateItemSettings(row.data),
                            cssClass: 'secondary',
                            onClick: (e) => this.onOpenFieldSettings(e, e.row.data),
                        },
                        {
                            icon: 'trash',
                            hint: this.translate.instant('Entfernen'),
                            visible: ({row}) => row.data.pimFields && row.data.pimFields.length > 0,
                            onClick: (e) => this.removeClicked(e.row.data),
                        }
                    ]
                }
            ];
        }
    }

    ngOnChanges(changes: SimpleChanges) {

    }

    readonly onRowClicked = (e: RowClickEvent) => {
        this.rowId = e.data.id;
        this.rowSelection = [e.data.id];
        this.rowSelected.emit(e.data.id);
    }

    async removeClicked(column: ExcelElement) {
        const yes = await confirm(
            this.translate.instant('Möchten Sie alle Felder, die mit dieser Spalte belegt wurden entfernen?'),
            this.translate.instant('Feld aus Template löschen')
        );
        if (yes) {
            this.removeTemplate.emit(column.id);
        }
    }

    getNpRowCount(rowCount: number, column: ExcelElement) {
        let increase = 0;
        if (column.calculation === 'FACTOR') increase++;
        return rowCount + increase;
    }

    getDxRowCount(rowCount: number, column: ExcelElement) {
        let increase = 0;
        return rowCount + increase;
    }

    hasFields(column: ExcelElement) {
        return column != undefined && column.pimFields != undefined && column.pimFields.length > 0;
    }

    getDragInfo(column: ExcelElement) {
        if (column.displayName != '') {
            return column.displayName;
        }

        if (this.hasFields(column)) {
            return column.pimFields
                .map((x) => (x.field + '_' + x.systemKey + '_' + x.elementKey).replace('_-1_-1', '').replace('_-1', ''))
                .join(' | ');
        } else {
            return column.title;
        }
    }

    toggleSettings(rowId: string) {
        var htmlBlock = document.getElementById('templateExcelDiv_' + rowId);
        var htmlPlus = document.getElementById('plus_' + rowId);
        if (htmlBlock != null) {
            if (this.exportMode) {
                if (htmlBlock.classList.contains('templateExcelExportColumnBig')) {
                    htmlBlock.classList.remove('templateExcelExportColumnBig');
                    htmlPlus.classList.remove('templatePlusRound');
                } else {
                    htmlBlock.classList.add('templateExcelExportColumnBig');
                    htmlPlus.classList.add('templatePlusRound');
                }
            } else {
                if (htmlBlock.classList.contains('templateExcelColumnBig')) {
                    htmlBlock.classList.remove('templateExcelColumnBig');
                    htmlPlus.classList.remove('templatePlusRound');
                } else {
                    htmlBlock.classList.add('templateExcelColumnBig');
                    htmlPlus.classList.add('templatePlusRound');
                }
            }
        }
    }

    updateNew(column: ExcelElement) {
        this.updateValue.emit(column.displayName);
    }

    public update(event: ValueChangedEvent, column?: ExcelElement, prop?: string) {
        if (column && prop) {
            column[prop] = event.value;
        }
        this.updateValue.emit(event.element.id.split('_').shift());
    }

    hasTemplateItemSettings(templateItem: ExcelElement) {
        if (!templateItem) return false;
        const hasCalculation = templateItem.calculation !== null && templateItem.calculation !== '';
        const hasNumberFormat = templateItem.numberformat !== null && templateItem.numberformat !== '';
        const hasSeparator = templateItem.seperator !== null && templateItem.seperator !== '';
        const hasMapping = templateItem.mapping !== null && templateItem.mapping !== '';
        const hasAutomaticSeparation = templateItem.maxCharacters > 0 && templateItem.section >= 1;
        const hasPrefixSuffix = templateItem.prefix?.trim() || templateItem.suffix?.trim();
        const hasFactor = templateItem.factorOperator && templateItem.calculation === 'FACTOR';
        return (
            hasCalculation ||
            hasNumberFormat ||
            hasSeparator ||
            hasMapping ||
            hasAutomaticSeparation ||
            hasPrefixSuffix ||
            hasFactor
        );
    }

    onOpenFieldSettings(event: ClickEvent, excelElement: ExcelElement) {
        this.popoverElement = excelElement;
        this.popoverTarget = event.element;
        this.popoverVisible = true;
    }

    onCloseFieldSettings(event: HidingEvent) {
        if (event.cancel) return;

        this.popoverElement = null;
        this.popoverTarget = null;
        this.popoverVisible = false;
    }

    readonly onDragStart = (e: RowDraggingStartEvent) => {
        e.itemData.source = 'excelModel';
        e.itemData.exportMode = this.exportMode;
    }

    readonly onDrop = (e: RowDraggingAddEvent) => {
        if (e.itemData.source != 'product') {
            return;
        }
        // TODO:
        // wenn man "einfügen an pos. X" realisieren will, müsste man in der methode,
        // die am ende durch dies event getriggert wird, unterscheiden, ob vorhanden (==> "replace")
        // oder nicht vorhanden (==> "add" am index X)

        this.replaceItem.emit(e);
    }

    readonly onReorder = (e: RowDraggingReorderEvent) => {
        this.reorderModel.emit(e);
    }

    private selectItemsFromSelectedPimFields(rebuildElements: boolean) {
        if (!rebuildElements)
            return;
        let relevantElements = this.excelModel.filter(x => x.pimFields.some(y => this.context.selectedElements.includes(y.field)));
        this.rowSelection = relevantElements.map(x => x.id);
    }
}
